import { AffiliatesTable } from '@/components/affiliate/table';
import { PageHeader, PageSurface } from '@/components/ui';
import { fetchProgramById, useProgramQuery } from '@/hooks/queries/use-affiliate-program-query';
import { ensureValidToken } from '@/libs/auth-guard';
import useNavBarStore from '@/stores/nav-store';
import { Container } from '@chakra-ui/react';
import type { Program } from '@fluenceur/types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { useLoaderData } from 'react-router-dom';

// Define the loader data structure
interface ProgramAffiliatesLoaderData {
  program: Program; // Program data will be fetched by loader
}

const ProgramAffiliatesPage = () => {
  const { t } = useTranslation();
  const { program: programFromLoader } = useLoaderData() as ProgramAffiliatesLoaderData;

  // Use the new query hook for the program itself, with initialData from loader
  const { data: program, isLoading: isProgramLoading } = useProgramQuery({
    programId: programFromLoader.id,
    initialData: programFromLoader,
  });

  const { setData } = useNavBarStore();

  const currentProgram = program || programFromLoader;

  // Seed the program panel's title. The panel is opened from the route.
  useEffect(() => {
    if (currentProgram) {
      setData({ id: currentProgram.id, name: currentProgram.name });
    }
  }, [currentProgram, setData]);

  if (isProgramLoading && !program) {
    return <p>{t('programs.programAffiliates.loadingProgram')}</p>;
  }

  if (!currentProgram) {
    return <p>{t('programs.programAffiliates.programNotFound')}</p>;
  }

  return (
    <PageSurface surfaceProps={{ overflow: 'auto' }}>
      <Container maxW="full" py={{ base: 5, xl: 6 }} px={{ base: 4, xl: 6 }}>
        <PageHeader
          title={t('programs.programAffiliates.title')}
          description={t('programs.programAffiliates.description')}
        />
        <AffiliatesTable programId={currentProgram.id} />
      </Container>
    </PageSurface>
  );
};

// Loader function to fetch only program data during route transition
export const programAffiliatesLoader = async ({ params }: LoaderFunctionArgs) => {
  await ensureValidToken();

  const { id } = params;
  if (!id) throw new Response('Program ID is required', { status: 400 });

  const programData = await fetchProgramById(id);

  if (!programData) {
    throw new Response('Program Not Found', { status: 404 });
  }

  return {
    program: programData,
  };
};

export default ProgramAffiliatesPage;
